import React from "react";
import "./Sidenav.scss";
import { SimpleTreeView as TreeView, TreeItem } from "@mui/x-tree-view";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import * as _ from "lodash";
import { RootState } from "../../store";
import { setBreadcrumbStack, toggleMenu } from "../../store/app/actions";
import useLocalStorage from "../hooks/useLocalStorage";
import { Icon, OutlinedInput, InputAdornment, CircularProgress, IconButton } from "./";
import { MenuItemIcon } from "./MenuItemIcon";
import { Search, TriangleDown, TriangleRight, ChevronLeft, ChevronRight } from "./icons";
import { BehaviorSubject } from "rxjs";
import { tap, debounceTime } from 'rxjs/operators';
import FooterImage from "../../assets/sidenav-footer.svg?react";
import { useSettings } from "../providers/SettingsProvider";
import clsx from "clsx";
import { SearchTermHighlight } from "./SearchTermHighlight";
import RequiresTags from "./RequiresTags";
import { useAuth } from "../../auth/AuthContext";
import { evaluateTagGroups } from "../utilities";
import logo from "../../assets/logo.svg";

export interface MenuItem {
    name?: string;
    title?: string | undefined;
    icon?: {
        type: "URL" | "BuiltIn" | "Binary";
        content: string;
    },
    description?: string | undefined;
    url?: string | undefined;
    children?: MenuItem[] | undefined;
    object?: "InsufficientPrivileges" | string;
    requiredTags?: string[][] | undefined;
}

function filterMenuItems(menuItem: MenuItem, searchTerm: string) {
    // Search term is blank or this node matches the search criteria...
    if (!searchTerm || menuItem.title?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
        return menuItem;
    }

    const survivors = _.chain(menuItem.children).map(a => filterMenuItems(a, searchTerm)).filter(a => !!a).value();

    if (survivors?.length > 0) {
        return {
            ...menuItem,
            children: survivors
        };
    } else {
        return null;
    }
}

function getFullHierarchy(item: MenuItem): MenuItem[] {
    const allChildren = _.flatMap(item.children, getFullHierarchy)
    return [item, ...allChildren];
}

function getPathToItem(searchTarget: MenuItem, currentItem: MenuItem, currentPath: MenuItem[] = []): MenuItem[] {
    if (currentItem === searchTarget) {
        return currentPath;
    }
    if (!currentItem.children) {
        return null;
    }

    return _.chain(currentItem.children).map(c => getPathToItem(searchTarget, c, [...currentPath, currentItem])).find(a => !!a).value();
}

// It gets complicated when you try to trim the branches midway, so instead we just cap the number of leaf nodes in the tree when rendering the search results.
function takeNLeafNodesFromTree(tree: MenuItem[], n: number) {
    let remainder = n;

    const getPrunedMenuItem = (item: MenuItem) => {
        if (!item.children?.length) {
            if (remainder > 0) {
                remainder--;
                return item;
            }
            else {
                return null;
            }
        }
        else {
            let trimmedChildren = item.children?.map(getPrunedMenuItem).filter(a => !!a);
            if (!trimmedChildren.length) {
                return null;
            }
            else {
                return { ...item, children: trimmedChildren };
            }
        }
    }

    return tree.map(getPrunedMenuItem).filter(a => !!a);
}

const searchTermSubject$ = new BehaviorSubject<string>("");

const CollapseExpandSideNavButton = (props: { toggleDrawer: (() => void), open: boolean }) => {
    const { toggleDrawer, open } = props;

    return (
        <IconButton onClick={toggleDrawer}>
            {
                open ?
                    <ChevronLeft size="medium" /> :
                    <ChevronRight size="medium" />
            }
        </IconButton >
    );
}

interface SidenavProps {
    toggleMenu: typeof toggleMenu;
    isLoading: boolean;
    menuItems: MenuItem[] | "loading";
    open: boolean;
    setBreadcrumbStack: typeof setBreadcrumbStack;
    activeItemContent?: React.ReactNode;
    className?: string;
}

function Sidenav(props: SidenavProps) {
    const { toggleMenu, isLoading, menuItems, open, setBreadcrumbStack, activeItemContent, className } = props;

    const [expandedNodes, setExpandedNodes] = useLocalStorage("side-nav-expanded-nodes", []);
    const searchField = React.useRef(null);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchTermText, setSearchTermText] = React.useState("");
    const [selectedNavItem, setSelectedNavItem] = React.useState<MenuItem>(null);
    const [filteredMenuItems, setFilteredMenuItems] = React.useState<React.ReactNode[]>([]);
    const [expandedSearchNodes, setExpandedSearchNodes] = React.useState<string[]>([]);
    const [highlightedNodes, setHighlightedNodes] = React.useState<MenuItem[]>([]);
    const location = useLocation();
    const settings = useSettings();
    const [width, setWidth] = React.useState(Number(localStorage.getItem("sidenav-width") ?? "400"));
    const { effectiveTags } = useAuth();

    React.useEffect(() => {
        if (menuItems === "loading") return;

        const allItems = _.flatMapDeep(menuItems, getFullHierarchy);
        const currentLocation = location.pathname;
        const activeItem = _.find(allItems, mi => mi?.url === currentLocation || mi?.url?.startsWith(currentLocation + "?")) as MenuItem;
        if (activeItem && activeItem !== selectedNavItem) {
            setSelectedNavItem(activeItem);
            setExpandedNodes(en => _.uniq([...en, activeItem.name]));
            const parentsToHighlight = _.chain(menuItems).map(a => getPathToItem(activeItem, a)).find(a => !!a).value();
            const breadcrumbStack = [...parentsToHighlight, activeItem]
            setHighlightedNodes(breadcrumbStack);
            setBreadcrumbStack(breadcrumbStack);
        }
    }, [menuItems, location]);

    const onExpandedItemsChange = (_event: any, nodeIds: string[]) => {
        if (!searchTerm) {
            setExpandedNodes(nodeIds);
        }
    }

    const renderMenuItemSubTree = (menuItem: MenuItem, depth = 1) => {
        if (menuItem.object === "InsufficientPrivileges" || !evaluateTagGroups(menuItem.requiredTags, effectiveTags)) {
            return null;
        }

        if (menuItem.children?.length) {
            return (
                <TreeItem key={menuItem.name} itemId={menuItem.name} className={`menu-group-level-${depth} parent ${highlightedNodes?.find(n => n?.name === menuItem?.name) ? "active" : ""}`} label={
                    <div className="menu-item-label">
                        {menuItem.icon && <MenuItemIcon {...menuItem.icon} />}
                        <SearchTermHighlight text={menuItem.title} searchTerm={searchTerm} />
                    </div>
                }>
                    {menuItem.children && menuItem.children.map(item => renderMenuItemSubTree(item, depth + 1)).filter(item => Boolean(item))}
                </TreeItem>
            );
        }
        else {
            return (
                <TreeItem key={menuItem.name} itemId={menuItem.name} className={`menu-group-level-${depth} ${highlightedNodes?.find(n => n?.name === menuItem?.name) ? "active leaf" : "leaf"}`} label={
                    <Link to={menuItem.url || "/not-found"}>
                        <div className="menu-item-label">
                            {menuItem.icon && <MenuItemIcon {...menuItem.icon} />}
                            <SearchTermHighlight text={menuItem.title} searchTerm={searchTerm} />
                        </div>
                    </Link>
                }>
                    {menuItem.name === selectedNavItem?.name && activeItemContent}
                </TreeItem>
            );
        }
    }

    React.useEffect(() => {
        const searchTermSubscription = searchTermSubject$
            .pipe(
                tap(setSearchTermText), // Immediately update the text in the input control...
                debounceTime(500)       // And debounce the update of the search term...
            ).subscribe(setSearchTerm);

        return () => searchTermSubscription.unsubscribe();
    }, []);

    React.useEffect(() => {
        if (menuItems === "loading") return;

        if (searchTerm) {
            let filteredResults: MenuItem[] = _.chain(menuItems).map(a => filterMenuItems(a, searchTerm)).filter(a => !!a).value();
            filteredResults = takeNLeafNodesFromTree(filteredResults, 200); // Cap the search results at 200 leaf nodes, for performance reasons...
            setFilteredMenuItems(filteredResults.map(item => renderMenuItemSubTree(item)).filter(item => Boolean(item)));
            setExpandedSearchNodes(_.chain(filteredResults).flatMapDeep(getFullHierarchy).filter(mi => mi?.children?.length > 0).map(mi => mi.name).value());
        }
        else {
            setFilteredMenuItems(menuItems.map(item => renderMenuItemSubTree(item)).filter(item => Boolean(item)));
            setExpandedSearchNodes([]);
        }
    }, [menuItems, selectedNavItem, highlightedNodes, searchTerm]);

    const handleResizerMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        const startX = e.pageX;
        const startWidth = width;

        const mouseMoveHandler = (e: MouseEvent) => {
            const newWidth = Math.max(100, startWidth + (e.pageX - startX));
            setWidth(newWidth);
            localStorage.setItem("sidenav-width", newWidth.toString());
        };

        const mouseUpHandler = () => {
            window.removeEventListener("mousemove", mouseMoveHandler);
            window.removeEventListener("mouseup", mouseUpHandler);
        };

        window.addEventListener("mousemove", mouseMoveHandler);
        window.addEventListener("mouseup", mouseUpHandler);
    };

    return (
        <div
            className={clsx("sidenav", open ? "open" : "closed", className)}
            style={{ width: width }}
        >
            <div className="collapse-expand-button">
                <CollapseExpandSideNavButton toggleDrawer={toggleMenu} open={open} />
            </div>
            <div className="drawer">
                <div className="drawer-header">
                    <div className="top-row">
                        <div className="logo-and-tenant-name">
                            <div className="logo-container">
                                <Link to="/">
                                    {
                                        settings.logoUrl ?
                                            <img id="logo" src={settings.logoUrl} alt="Company Logo" className="logo tenant-logo" /> :
                                            <img id="logo" src={logo} alt="The Qerent Logo" className="logo" />
                                    }
                                </Link>
                            </div>
                            <div className="tenant-name">
                                {settings.tenantName}
                            </div>
                        </div>
                    </div>
                    <div className="nav-tree">
                        <RequiresTags tags={[["users::read"], ["roles::read"]]}>
                            <Link to={"/admin"}>
                                <div className="menu-item-label">
                                    <Icon>settings</Icon>
                                    Administration
                                </div>
                            </Link>
                        </RequiresTags>
                    </div>
                    <div className="search-row">
                        <OutlinedInput
                            size="small"
                            placeholder="Search"
                            className="search-menu-items-input"
                            inputRef={searchField}
                            fullWidth
                            startAdornment={
                                <InputAdornment position="start">
                                    <Search size="small" />
                                </InputAdornment>
                            }
                            value={searchTermText}
                            onChange={(event) => searchTermSubject$.next(event.target.value)}
                        />
                    </div>
                    <hr />

                </div>
                <div className="menu-items">
                    {
                        menuItems === "loading" || isLoading ?
                            <div><CircularProgress className="loading-spinner-inline" /></div> :
                            filteredMenuItems?.length > 0 ?
                                <TreeView
                                    className="nav-tree"
                                    // By updating the key whenever the search term changes, the TreeView doesn't need to worry about collapsing/expanding nodes (with the subsequent layout thrashing)...
                                    key={`menu-tree-${searchTerm}`}
                                    onExpandedItemsChange={onExpandedItemsChange}
                                    expandedItems={!searchTerm ? expandedNodes : expandedSearchNodes}
                                    disableSelection={true}
                                    slots={{
                                        collapseIcon: (params) => !searchTerm ? <TriangleDown {...params} size="small" /> : null,
                                        expandIcon: (params) => !searchTerm ? <TriangleRight {...params} size="small" /> : null
                                    }}
                                    itemChildrenIndentation={"0px"}
                                >
                                    {filteredMenuItems}
                                </TreeView> :
                                <div className="no-items">No items found.</div>
                    }
                </div>
                <div className="drawer-footer">
                    <FooterImage />
                </div>
            </div>

            <div
                className="resize-handle"
                onMouseDown={handleResizerMouseDown}
            />
        </div>
    );
}

export default connect(
    (state: RootState) => ({
        isLoading: state.app.loading,
        open: state.app.menuIsExpanded
    }),
    { setBreadcrumbStack, toggleMenu })(Sidenav);